import React, { useState } from 'react';

import classNames from 'classnames';

import { Close } from '@uikit/icons/Close';

import { GET_REWARD_POOL_LEADERBOARD_RAFFLE_MODE } from '@shared/api/rewardPools';
import { RewardPool } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { getDistributionPolicyFlags } from '@shared/entities/rewardPool';
import {
  useRewardPoolLeaderboardInfinityQuery,
  useSelfRewardPoolLeaderboardDataQuery,
} from '@shared/hooks/query/rewardPools';

import * as Dialog from '@radix-ui/react-dialog';
import { isRewardPoolCompleted } from '@store:web/components/entities/rewardPool';

import { LeaderboardNew } from './LeaderboardNew/LeaderboardNew';
import RewardPoolLeaderboardTable from './RewardPoolLeaderboardTable';

interface RewardPoolLeaderboardDialogProps extends Dialog.DialogProps {
  rewardPool: RewardPool;
}

const RewardPoolLeaderboardDialog = ({
  rewardPool,
  open,
  ...props
}: RewardPoolLeaderboardDialogProps) => {
  const [showWinners, setShowWinners] = useState(false);
  const { isAuth } = useAuth();

  const { isRaffleV2, isRaffle, isProRata, isRaffleSwap } = getDistributionPolicyFlags(
    rewardPool.distribution_policy,
  );

  const raffleMode =
    isRaffle && showWinners
      ? GET_REWARD_POOL_LEADERBOARD_RAFFLE_MODE.WINNERS
      : GET_REWARD_POOL_LEADERBOARD_RAFFLE_MODE.ALL;

  const leaderboardQuery = useRewardPoolLeaderboardInfinityQuery(
    {
      rewardPoolId: rewardPool.id,
      params: {
        raffleMode,
      },
    },
    {
      enabled: open,
      staleTime: 0,
    },
  );

  const leaderboardMeQuery = useSelfRewardPoolLeaderboardDataQuery(rewardPool.id, {
    enabled: open && isAuth,
  });

  return (
    <Dialog.Root open={open} {...props}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay">
          <Dialog.Content aria-describedby={undefined} className="dialog msq-dialog-content-lg">
            <div className="dialog-header">
              <Dialog.Title className="dialog-title">
                {isRaffle ? (
                  <>Participants & Winners</>
                ) : (
                  <>{rewardPool.participants_counter} Participants</>
                )}
              </Dialog.Title>

              <Dialog.Close asChild>
                <button className="dialog-close">
                  <Close className="dialog-close-icon" />
                </button>
              </Dialog.Close>
            </div>

            <div className="dialog-body p-0">
              {isRaffle && (
                <div className="dialog-body-p pb-0 sticky bg-neutral-50 top-0">
                  <div className="toggle-button-group">
                    <button
                      onClick={() => setShowWinners(true)}
                      disabled={!isRewardPoolCompleted(rewardPool)}
                      className={classNames('toggle-button w-full', {
                        'toggle-button-active': showWinners,
                      })}
                    >
                      Winners
                    </button>
                    <button
                      onClick={() => setShowWinners(false)}
                      className={classNames('toggle-button w-full', {
                        'toggle-button-active': !showWinners,
                      })}
                    >
                      Participants
                    </button>
                  </div>
                </div>
              )}

              <div className="dialog-body-p pb-0">
                {isRaffleV2 || isRaffleSwap || isProRata ? (
                  <div className="py-4">
                    <LeaderboardNew
                      raffleMode={raffleMode}
                      rewardPool={rewardPool}
                      leaderboardMeQuery={leaderboardMeQuery}
                      leaderboardQuery={leaderboardQuery}
                    />
                  </div>
                ) : (
                  <RewardPoolLeaderboardTable
                    rewardPool={rewardPool}
                    leaderboardMeQuery={leaderboardMeQuery}
                    leaderboardQuery={leaderboardQuery}
                    showRankWithDistribution={isProRata}
                  />
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RewardPoolLeaderboardDialog;
